import { useEffect, useState } from "react";
import { Eyebrow, Reveal } from "./primitives.jsx";
import { useSiteContent } from "../content.js";
import { MAIN_HOST } from "../siteAddress.js";
import { BOOTH_STILLS } from "../data.js";
import { TEAM } from "../booth/teams.js";
import {
  dayET,
  dayWords,
  scoreWords,
  seriesView,
  standingWords,
  timeET,
  useNow,
  useSeries,
  winnerOf,
} from "../booth/series.js";

// Greg's live booth on the home page: what it is, the World Series schedule
// with his nights marked (every game, from /api/booth/series), a few stills
// from past broadcasts, and the three things to know (where to watch, that he
// reads your questions, how to get an email when he goes live). The hero
// above carries the countdown (hero-next.jsx); this is the part you read.

function Chip({ abbr }) {
  const [t, t2] = TEAM[abbr] || ["#1a3658", "#f4f1ea"];
  return (
    <span className="booth-chip" style={{ "--t": t, "--t2": t2 }}>
      {abbr}
    </span>
  );
}

const channel = (tv) => (tv ? String(tv).split("/")[0].trim() : "");

function Team({ side, score, won, lost }) {
  return (
    <span className={`booth-team${won ? " is-win" : ""}${lost ? " is-loss" : ""}`}>
      <Chip abbr={side.abbr} />
      <b>{side.club}</b>
      {score != null && <span className="booth-score">{score}</span>}
    </span>
  );
}

function Game({ g, v, now }) {
  const isNext = v.phase === "next" && g.pk === v.game.pk;
  const isOn = v.phase === "on" && g.pk === v.game.pk;
  const w = winnerOf(v, g);
  const scored = g.state === "final" || isOn;
  const tag = isOn
    ? g.inning || "Live now"
    : g.state === "final"
      ? "Final"
      : g.state === "postponed"
        ? "Postponed"
        : g.state === "unneeded"
          ? "Not needed"
          : isNext
            ? dayWords(g.at, now)
            : g.ifNecessary
              ? "If needed"
              : dayWords(g.at, now);
  const foot =
    g.state === "final"
      ? g.standing || scoreWords(g)
      : g.state === "unneeded"
        ? "Series over"
        : g.tbd
          ? `${dayET(g.at)} · time TBA`
          : [dayET(g.at), timeET(g.at), channel(g.tv)].filter(Boolean).join(" · ");
  return (
    <li
      className={`booth-game is-${g.state}${isNext ? " is-next" : ""}${isOn ? " is-on" : ""}`}
      aria-current={isNext || isOn ? "true" : undefined}
    >
      <span className="booth-game-top">
        <span className="booth-game-no">Game {g.no}</span>
        <span className="booth-game-tag">{tag}</span>
      </span>
      <span className="booth-game-teams">
        <Team
          side={g.away}
          score={scored ? (g.away.score ?? 0) : null}
          won={w && w.id === g.away.id}
          lost={w && w.id !== g.away.id}
        />
        <Team
          side={g.home}
          score={scored ? (g.home.score ?? 0) : null}
          won={w && w.id === g.home.id}
          lost={w && w.id !== g.home.id}
        />
      </span>
      <span className="booth-game-foot">{foot}</span>
      {(isNext || isOn) && (
        <span className="booth-game-greg">
          <i aria-hidden="true" />
          {isOn ? "Greg's night" : "Greg's calling it"}
        </span>
      )}
    </li>
  );
}

function Schedule({ v, now, note }) {
  const s = v.series;
  const standing = standingWords(v);
  const [a, b] = v.clubs;
  const over = v.phase === "over";
  const kicker = over
    ? `${s.name} ${s.season} · ${standing}`
    : `${s.name} ${s.season} · ${a.club} vs. ${b.club}${standing ? ` · ${standing}` : ""}`;
  return (
    <div className={`booth-series${over ? " is-over" : ""}`}>
      <div className="booth-series-head">
        <span className="booth-kicker mono">{kicker}</span>
        <h3 className="booth-series-title">
          {over
            ? `That's a wrap on the ${s.season} Series. Thanks for sitting with Greg.`
            : "Greg's in the booth for every game. Here's the slate."}
        </h3>
      </div>
      <ol className="booth-games" aria-label={`${s.name} schedule`}>
        {v.games.map((g) => (
          <Game key={g.pk} g={g} v={v} now={now} />
        ))}
      </ol>
      {!over && note && <p className="booth-note mono">{note}</p>}
    </div>
  );
}

function Stills() {
  return (
    <div className="booth-stills-wrap">
      <span className="booth-kicker mono">From past nights in the booth</span>
      <div className="booth-stills" aria-label="From past broadcasts">
        {BOOTH_STILLS.map((s) => (
          <figure className="booth-still" key={s.src}>
            <picture>
              <source srcSet={s.src.replace(/\.jpe?g$/i, ".webp")} type="image/webp" />
              <img
                src={s.src}
                alt={s.alt}
                loading="lazy"
                decoding="async"
                width="960"
                height="540"
              />
            </picture>
            <figcaption>
              <b>{s.cap}</b>
              <span className="mono">{s.when}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

// Same list as /live's "Get an email next time" (and its localStorage key,
// so a reader who signed up there sees they're on it here).
const ALERTS_KEY = "bx-mail";
function Alerts({ title, text }) {
  const [state, setState] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    try {
      if (localStorage.getItem(ALERTS_KEY) === "on") setState("on");
    } catch {}
  }, []);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await fetch("/api/booth/alerts", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || "That didn't go through. Try again.");
      setState("check");
    } catch (x) {
      setErr(x.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="booth-how-card booth-alerts">
      <span className="booth-how-n mono">03</span>
      <h4>{title}</h4>
      {state === "on" ? (
        <p className="booth-alerts-done">
          <b>You're on the list.</b> We'll email you when Greg goes live.
        </p>
      ) : state === "check" ? (
        <p className="booth-alerts-done">
          <b>Check your inbox.</b> Tap the link in the email to confirm.
        </p>
      ) : (
        <>
          <p>{text}</p>
          <form className="booth-alerts-form" onSubmit={send}>
            <label className="sr-only" htmlFor="booth-alerts-email">
              Email address
            </label>
            <input
              id="booth-alerts-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <button type="submit" disabled={busy || !email.trim()}>
              {busy ? "Sending…" : "Email me"}
            </button>
          </form>
          {err && <p className="booth-alerts-err">{err}</p>}
        </>
      )}
    </div>
  );
}

export default function BoothSection() {
  const { booth: copy } = useSiteContent();
  const data = useSeries();
  const now = useNow(30_000);
  const v = seriesView(data, now);
  return (
    <section id="booth" data-screen-label="01 Greg's Booth" className="section booth">
      <div className="section-inner">
        <div className="booth-head">
          <Reveal>
            <Eyebrow index="01">{copy.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="display h-lg booth-title">
              {copy.titlePrefix} <em>{copy.titleEmphasis}</em>
              <br />
              {copy.titleSuffix}
            </h2>
          </Reveal>
          <Reveal delay={240}>
            <p className="lede booth-lede">{copy.lede}</p>
          </Reveal>
        </div>

        {v.phase !== "none" && (
          <Reveal delay={200}>
            <Schedule v={v} now={now} note={copy.note} />
          </Reveal>
        )}

        {BOOTH_STILLS.length > 0 && (
          <Reveal delay={240}>
            <Stills />
          </Reveal>
        )}

        <Reveal delay={260}>
          <div className="booth-how">
            <a className="booth-how-card booth-how-link" href="/live">
              <span className="booth-how-n mono">01</span>
              <h4>{copy.watchTitle}</h4>
              <p>{copy.watchText}</p>
              <span className="booth-how-go mono">{MAIN_HOST}/live →</span>
            </a>
            <div className="booth-how-card">
              <span className="booth-how-n mono">02</span>
              <h4>{copy.askTitle}</h4>
              <p>{copy.askText}</p>
            </div>
            <Alerts title={copy.alertsTitle} text={copy.alertsText} />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const css = `
.booth {
  position: relative;
  padding: 72px 0 64px;
  background:
    radial-gradient(70% 50% at 50% 0%, rgba(0, 70, 135, 0.22), transparent 70%),
    var(--navy-900);
}
.booth-head { max-width: 860px; }
.booth-title { margin: 20px 0 22px; }
.booth-lede { max-width: 62ch; margin: 0; }

/* The slate. */
.booth-series { margin-top: 44px; }
.booth-series-head { display: grid; gap: 10px; margin-bottom: 18px; }
.booth-kicker { font-size: 0.6875rem; letter-spacing: 0.18em; text-transform: uppercase; color: var(--gold); }
.booth-series-title { margin: 0; font: 500 clamp(1.25rem, 1.8vw, 1.625rem)/1.25 var(--serif); letter-spacing: -0.01em; color: var(--bone); max-width: 40ch; }
.booth-games {
  list-style: none; margin: 0; padding: 4px 0 8px;
  display: grid; grid-auto-flow: column; grid-auto-columns: minmax(152px, 1fr); gap: 8px;
  overflow-x: auto; scroll-snap-type: x proximity; scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}
.booth-game {
  position: relative; display: grid; gap: 12px; align-content: start;
  padding: 15px 14px 13px;
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(16, 38, 65, 0.55), rgba(10, 25, 41, 0.7));
  box-shadow: inset 0 0 0 1px rgba(189, 155, 96, 0.18);
  scroll-snap-align: start;
  min-width: 0;
  transition: box-shadow 200ms ease, transform 200ms ease;
}
.booth-game.is-final { background: linear-gradient(180deg, rgba(10, 25, 41, 0.6), rgba(5, 13, 26, 0.7)); }
.booth-game.is-upcoming.is-next, .booth-game.is-postponed.is-next {
  background: linear-gradient(180deg, rgba(0, 100, 194, 0.42), rgba(0, 70, 135, 0.4));
  box-shadow: inset 0 0 0 1px rgba(43, 138, 255, 0.7), 0 20px 50px -24px rgba(43, 138, 255, 0.7);
}
.booth-game.is-on { background: linear-gradient(180deg, rgba(200, 16, 46, 0.38), rgba(80, 8, 20, 0.5)); box-shadow: inset 0 0 0 1px rgba(255, 74, 61, 0.75), 0 20px 50px -24px rgba(255, 74, 61, 0.7); }
.booth-game.is-unneeded, .booth-game.is-cancelled { opacity: 0.45; }
.booth-game-top { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
.booth-game-no { font: 600 0.6875rem/1 var(--mono); letter-spacing: 0.16em; text-transform: uppercase; color: var(--bone); }
.booth-game-tag { font: 500 0.625rem/1 var(--mono); letter-spacing: 0.12em; text-transform: uppercase; color: var(--gold); white-space: nowrap; }
.booth-game.is-next .booth-game-tag { color: #fff; }
.booth-game.is-on .booth-game-tag { color: #ff8a80; }
.booth-game.is-final .booth-game-tag { color: var(--bone-dim); opacity: 0.7; }
.booth-game-teams { display: grid; gap: 7px; }
.booth-team { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 8px; }
.booth-team b { font: 500 1rem/1.1 var(--serif); letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.booth-team.is-loss b, .booth-team.is-loss .booth-score { opacity: 0.5; }
.booth-score { font: 400 1.5rem/0.8 var(--crt); color: #ffd98a; font-variant-numeric: tabular-nums; }
.booth-game.is-on .booth-score { text-shadow: 0 0 10px rgba(255, 196, 86, 0.5); }
.booth-chip { display: inline-grid; place-items: center; min-width: 2.6em; padding: 0.28em 0.4em; border-radius: 3px; background: var(--t); color: var(--t2); font: 700 0.5625rem/1 var(--sans); letter-spacing: 0.05em; }
.booth-game-foot { font: 500 0.625rem/1.4 var(--mono); letter-spacing: 0.06em; color: var(--bone-dim); opacity: 0.8; }
.booth-game-greg { display: inline-flex; align-items: center; gap: 7px; font: 600 0.5625rem/1 var(--mono); letter-spacing: 0.14em; text-transform: uppercase; color: #ff8a80; }
.booth-game-greg i { width: 6px; height: 6px; border-radius: 50%; background: #ff4a3d; box-shadow: 0 0 8px rgba(255, 74, 61, 0.8); }
.booth-note { margin: 14px 0 0; font-size: 0.6875rem; letter-spacing: 0.08em; color: var(--bone-dim); opacity: 0.75; }
.booth-series.is-over .booth-games { opacity: 0.9; }

/* Stills from past nights. */
.booth-stills-wrap { display: grid; gap: 14px; margin-top: 40px; }
.booth-stills { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
.booth-still { margin: 0; position: relative; border-radius: 12px; overflow: hidden; background: #02060c; box-shadow: inset 0 0 0 1px rgba(189, 155, 96, 0.2); aspect-ratio: 16 / 9; }
.booth-still picture, .booth-still img { width: 100%; height: 100%; display: block; }
.booth-still img { object-fit: cover; transition: transform 500ms cubic-bezier(0.2, 0.8, 0.2, 1); }
.booth-still:hover img { transform: scale(1.03); }
.booth-still figcaption { position: absolute; left: 0; right: 0; bottom: 0; display: grid; gap: 3px; padding: 28px 12px 10px; background: linear-gradient(180deg, transparent, rgba(5, 13, 26, 0.9)); }
.booth-still figcaption b { font: 500 0.875rem/1.2 var(--serif); color: var(--bone); }
.booth-still figcaption span { font-size: 0.5625rem; letter-spacing: 0.14em; text-transform: uppercase; color: var(--gold); }

/* The three things to know. */
.booth-how { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-top: 36px; }
.booth-how-card { position: relative; display: grid; gap: 10px; align-content: start; padding: 22px 22px 20px 22px; border-radius: 14px; background: rgba(10, 25, 41, 0.55); box-shadow: inset 0 0 0 1px var(--rule); color: inherit; text-decoration: none; transition: box-shadow 200ms ease, transform 200ms ease; }
.booth-how-link:hover { box-shadow: inset 0 0 0 1px var(--gold); transform: translateY(-2px); }
.booth-how-n { font-size: 0.625rem; letter-spacing: 0.2em; color: var(--gold); }
.booth-how-card h4 { margin: 0; font: 500 1.25rem/1.15 var(--serif); letter-spacing: -0.01em; color: var(--bone); }
.booth-how-card p { margin: 0; font-size: 0.9375rem; line-height: 1.5; color: var(--bone-dim); }
.booth-how-go { margin-top: 6px; font-size: 0.6875rem; letter-spacing: 0.12em; color: var(--royal-blue-glow); }
.booth-alerts-form { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 8px; margin-top: 4px; }
.booth-alerts-form input { min-width: 0; padding: 11px 12px; border-radius: 8px; border: 1px solid rgba(189, 155, 96, 0.35); background: rgba(5, 13, 26, 0.7); color: var(--bone); font: 500 0.9375rem/1.2 var(--sans); }
.booth-alerts-form input::placeholder { color: rgba(232, 227, 214, 0.45); }
.booth-alerts-form input:focus-visible { outline: 2px solid var(--royal-blue-glow); outline-offset: 1px; border-color: transparent; }
.booth-alerts-form button { padding: 11px 16px; border-radius: 8px; border: 0; background: var(--gold); color: var(--navy-900); font: 600 0.875rem/1.2 var(--sans); cursor: pointer; white-space: nowrap; }
.booth-alerts-form button:hover { background: var(--gold-bright); }
.booth-alerts-form button:disabled { opacity: 0.5; cursor: default; }
.booth-alerts-done { margin: 0; font-size: 0.9375rem; line-height: 1.5; color: var(--bone-dim); }
.booth-alerts-done b { color: var(--bone); }
.booth-alerts-err { margin: 0; font-size: 0.8125rem; color: #ff8a80; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

@media (max-width: 1080px) {
  .booth-games { grid-auto-columns: minmax(172px, 46%); }
  .booth-stills { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .booth-how { grid-template-columns: minmax(0, 1fr); }
  .booth-how-card { padding: 18px 18px 16px; }
}
@media (max-width: 768px) {
  .booth { padding: 56px 0 48px; }
  .booth-series { margin-top: 32px; }
  .booth-games { grid-auto-columns: minmax(168px, 72%); gap: 8px; margin: 0 -24px; padding: 4px 24px 8px; scroll-padding: 0 24px; }
  .booth-stills-wrap { margin-top: 30px; gap: 10px; }
  .booth-stills { gap: 8px; }
  .booth-how { margin-top: 28px; gap: 8px; }
}
@media (prefers-reduced-motion: reduce) {
  .booth-still img, .booth-how-card, .booth-game { transition: none; }
}
`;
if (typeof document !== "undefined") {
  const el = document.createElement("style");
  el.dataset.section = "booth";
  el.textContent = css;
  document.head.appendChild(el);
}
